import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LoadingScreen from "@components/LoadingScreen/LoadingScreen";
import FullLayout from "@components/layouts/FullLayout";
import { BoardProvider } from "@contexts/BoardContext";
import { BoardFiltersProvider } from "@contexts/BoardFiltersContext";
import BoardWithSidebarLayout from "@features/board/board-layout/BoardWithSidebarLayout";
import BoardFullWidthLayout from "@features/board/board-layout/BoardFullWidthLayout";
import BoardLastViewRedirect from "@features/board/preferences/BoardLastViewRedirect";

// Lazy load route components
const LoadingPage = lazy(() => import("@features/core/LoadingPage"));
const OidcCallbackPage = lazy(() => import("@features/auth/OidcCallbackPage"));
const TeamsAuthStartPage = lazy(
  () => import("@features/auth/TeamsAuthStartPage"),
);
const SignInStartPage = lazy(() => import("@features/auth/SignInStartPage"));
const RegionSelectionPage = lazy(
  () => import("@features/region-selection/RegionSelectionPage"),
);
const Dashboard = lazy(() => import("@features/dashboard/Dashboard"));
const BoardView = lazy(() => import("@features/board/board-view/BoardView"));
const SchedulePage = lazy(
  () => import("@features/board/schedule/SchedulePage"),
);
const Connections = lazy(
  () => import("@features/board/connections/Connections"),
);
const AddConnectionPage = lazy(
  () => import("@features/board/connections/catalogue/AddConnectionPage"),
);
const EditConnectionPage = lazy(
  () => import("@features/board/connections/edit/EditConnectionPage"),
);
const TaskFormPage = lazy(
  () => import("@features/task-form/TaskFormPage/TaskFormPage"),
);
const BoardSettings = lazy(
  () => import("@features/board/settings/BoardSettings"),
);
const Numbers = lazy(() => import("@features/board/numbers/Numbers"));
const HiddenTasks = lazy(
  () => import("@features/board/hidden-tasks/HiddenTasks"),
);
const AccountPage = lazy(() => import("@features/account/AccountPage"));
const BillingEntryPage = lazy(() => import("@features/billing/BillingEntryPage"));
const BillingReturnPage = lazy(() => import("@features/billing/BillingReturnPage"));
const AuthPopupCallbackPage = lazy(
  () => import("@/features/auth-popup/AuthPopupCallback"),
);
const AADAdminConsentPage = lazy(
  () => import("@/features/auth-popup/oauth2/AADAdminConsent"),
);
const AdminConsentPage = lazy(
  () => import("@features/auth-popup/oauth2/AdminConsentPage"),
);

/**
 * Main app router
 *
 * Uses React Router v6 with:
 * - Lazy loading for code splitting
 * - Layout wrapper (FullLayout)
 * - Protected routes via layout authentication check
 *
 * Routes match the Angular app structure:
 * - / -> /loading (initializes boards)
 * - /loading -> LoadingPage (bootstrap logic)
 * - /dashboard -> Dashboard view
 * - /:boardId/* -> Board view with nested routes
 *
 * All data fetching uses hooks from @onetasklist/shared
 */
/**
 * Layout route that provides boardId via BoardContext to all board-feature routes.
 * Reads boardId from the :boardId route param once, so child viewmodels don't need useParams.
 */
const BoardLayout: React.FC = () => (
  <BoardProvider>
    <BoardFiltersProvider>
      <BoardWithSidebarLayout />
    </BoardFiltersProvider>
  </BoardProvider>
);

const BoardFullWidthBoardLayout: React.FC = () => (
  <BoardProvider>
    <BoardFiltersProvider>
      <BoardFullWidthLayout />
    </BoardFiltersProvider>
  </BoardProvider>
);

const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/oidc-callback" element={<OidcCallbackPage />} />
        <Route path="/teams-auth-start" element={<TeamsAuthStartPage />} />
        <Route path="/login" element={<SignInStartPage />} />
        <Route path="/signup" element={<SignInStartPage signUp />} />
        <Route
          path="/auth/popup/callback"
          element={<AuthPopupCallbackPage />}
        />
        <Route
          path="/auth/popup/oauth2/aad-admin-consent"
          element={<AADAdminConsentPage />}
        />
        <Route
          path="/auth/popup/oauth2/admin-consent"
          element={<AdminConsentPage />}
        />
        <Route path="/regions" element={<RegionSelectionPage />} />
        <Route path="/loading" element={<LoadingPage />} />
        {/* The website's pricing buttons, and Stripe's return. Never under /account, which Lobby sends to Login. */}
        <Route path="/billing" element={<BillingEntryPage />} />
        <Route path="/billing/return" element={<BillingReturnPage />} />

        <Route path="/" element={<FullLayout />}>
          {/* Default route redirects to loading */}
          <Route index element={<Navigate to="/loading" replace />} />

          {/* Dashboard */}
          <Route path="dashboard" element={<Dashboard />} />

          {/* Account settings */}
          <Route path="account" element={<AccountPage />} />

          {/* Board routes — all share BoardContext via BoardLayout */}
          <Route path=":boardId" element={<BoardLayout />}>
            <Route index element={<BoardLastViewRedirect />} />
            <Route path="tasks" element={<BoardView />} />
            <Route path="schedule" element={<SchedulePage />} />
          </Route>

          {/* Full-width board routes (no sidebar) */}
          <Route path=":boardId" element={<BoardFullWidthBoardLayout />}>
            <Route path="connections" element={<Connections />} />
            <Route path="settings" element={<BoardSettings />} />
            <Route path="numbers" element={<Numbers />} />
            <Route path="hidden" element={<HiddenTasks />} />
            <Route path="hiddentasks" element={<HiddenTasks />} />
            <Route path="connections/new" element={<AddConnectionPage />} />
            <Route
              path="connections/:connectionId/edit"
              element={<EditConnectionPage />}
            />
            <Route path="tasks/:taskId" element={<TaskFormPage />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/loading" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
